import { isDunningDocumentTextType } from '../../invoices/utils/dunningDocument';
import { isIncomingInvoiceDocumentType } from '../../invoices/utils/incomingInvoice';
import { mailDispatchPurposeForDocumentLabel, type MailDispatchPurpose } from '../../../types/mailDispatch';
import { formatDateToDDMMYYYY } from '../../../utils/date';
import { getLeadDocumentUrl, type LeadDocument } from '../../projects/utils/leadDocumentFiles';
import { documentTypeLabelDe } from './finalInvoiceDeduction';
import { applySendPdfEmailBodyTemplate, type SendPdfEmailTemplateValues } from './sendPdfEmailBodyTemplate';

/** Belegarten mit eigener Textvorlage (Einstellungen → Textvorlagen). */
export const DOCUMENT_TYPES_FOR_DOCUMENT_TEXTS = [
  'Angebot',
  'Kostenvoranschlag',
  'Auftragsbestätigung',
  'Lieferschein',
  'Rechnung',
  'Abschlagsrechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
  'Erinnerung',
  '1. Mahnung',
  '2. Mahnung',
  '3. Mahnung',
] as const;

export type DocumentTextType = (typeof DOCUMENT_TYPES_FOR_DOCUMENT_TEXTS)[number];

export function isDocumentTextType(value: unknown): value is DocumentTextType {
  return typeof value === 'string' && (DOCUMENT_TYPES_FOR_DOCUMENT_TEXTS as readonly string[]).includes(value);
}

/**
 * `invoice_documents.document_type` → Textvorlagen-Typ.
 * Eingangsrechnungen und Unbekanntes haben keine Vorlage (neutraler Text).
 */
export function documentTextTypeFromInvoiceDocumentType(
  documentType: string | null | undefined,
): DocumentTextType | null {
  const value = String(documentType ?? '').trim();
  if (!value || isIncomingInvoiceDocumentType(value)) return null;
  if (value === 'offer') return 'Angebot';
  const label = documentTypeLabelDe(value);
  return isDocumentTextType(label) ? label : null;
}

export function mailPurposeForDocumentTextType(type: DocumentTextType | null | undefined): MailDispatchPurpose {
  if (!type) return 'general';
  if (isDunningDocumentTextType(type)) return 'dunning';
  return mailDispatchPurposeForDocumentLabel(type);
}

/** Mit Cent wie auf dem Beleg (`formatMoney` rundet auf ganze Euro). */
function formatInvoiceAmount(value: number | string | null | undefined, currency: string): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(n);
}

export function documentMailTemplateValues(doc: {
  invoice_number?: string | null;
  issue_date?: string | null;
  due_date?: string | null;
  gross_amount?: number | string | null;
  currency?: string | null;
}): SendPdfEmailTemplateValues {
  return {
    invoiceNumber: (doc.invoice_number ?? '').trim(),
    date: doc.issue_date ? formatDateToDDMMYYYY(doc.issue_date) : '',
    amount: formatInvoiceAmount(doc.gross_amount, doc.currency || 'EUR'),
    dueDate: doc.due_date ? formatDateToDDMMYYYY(doc.due_date) : '',
  };
}

/** Optionen für `sharePdfByMail` aus einem Projektdokument (Belegart über den verknüpften Beleg). */
export function leadDocumentMailShareOptions(leadId: string | number, doc: LeadDocument) {
  const linked = doc.linked_document ?? null;
  return {
    url: getLeadDocumentUrl(leadId, doc),
    filename: doc.filename,
    leadId,
    documentTextType: documentTextTypeFromInvoiceDocumentType(linked?.document_type),
    templateValues: linked ? documentMailTemplateValues(linked) : undefined,
  };
}

function filenameWithoutExtension(filename: string): string {
  return filename.trim().replace(/\.pdf$/i, '');
}

/**
 * Betreff und Text für „Per E-Mail senden“: mit Belegart die Textvorlage aus den
 * Einstellungen, sonst ein neutraler Text (kein „unser Angebot“ für beliebige Dateien).
 */
export async function buildDocumentMailDraft(opts: {
  documentTextType: DocumentTextType | null | undefined;
  filename: string;
  greeting: string;
  values?: SendPdfEmailTemplateValues;
}): Promise<{ subject: string; body: string }> {
  const { documentTextType: label, filename, greeting, values = {} } = opts;
  if (!label) {
    return {
      subject: filenameWithoutExtension(filename),
      body: `${greeting}\n\nanbei erhalten Sie das Dokument „${filename.trim()}“.`,
    };
  }

  let template: string | null = null;
  try {
    const res = await fetch(`/api/settings/document-texts?type=${encodeURIComponent(label)}`, {
      credentials: 'include',
    });
    if (res.ok) {
      const texts = (await res.json()) as { sendPdfEmailBodyTemplate?: string };
      template = texts.sendPdfEmailBodyTemplate ?? null;
    }
  } catch {
    /* Standardtext */
  }

  const number = (values.invoiceNumber ?? '').trim();
  return {
    subject: number ? `${label} ${number}` : filenameWithoutExtension(filename),
    body: applySendPdfEmailBodyTemplate(template, greeting, label, values),
  };
}
